import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';

// Knox i18n resources for the 'knox-aiops' namespace.
const KNOX_RESOURCES_EN: Record<string, Record<string, unknown>> = {
  'knox-aiops': {
    nav: {
      brand: 'Knox AI-Native AIOps',
      triageInbox: 'Triage Inbox',
      members: 'Members',
      selectRoom: 'Select a room to start',
      settings: 'Settings',
      signOut: 'Sign Out',
      support: 'Support',
      lightMode: 'Light Mode',
      darkMode: 'Dark Mode',
      expandSidebar: 'Expand sidebar',
      collapseSidebar: 'Collapse sidebar',
      newRoom: 'New Room',
      inbox: 'Inbox',
      inboxPending: 'pending items',
      searchRooms: 'Search rooms...',
      searchRoomsAria: 'Search rooms',
      clearSearch: 'Clear search',
      rooms: 'Rooms',
      noRoomsFound: 'No rooms found',
      noRoomsYet: 'No rooms yet',
      createFirstRoom: 'Create your first room',
      showingRoomsOf: 'Showing {{shown}} of {{total}} rooms — use search to find more',
      agents: 'Knox Daemon',
    },
    login: {
      signIn: 'Sign in to Knox',
      subtitle: 'Your AI-powered IT crew is ready',
      invitationCode: 'Have an invitation code?',
      invitationCodeLabel: 'Invitation Code',
      invitationCodePlaceholder: 'Enter your code',
      go: 'Go',
      copyright: '© 2026 Knox',
      termsOfService: 'Terms of Service',
      privacyPolicy: 'Privacy Policy',
      oauthError: {
        KNOX_AUTH_OAUTH_NO_ORG: 'Your account is not associated with any organization. Please register with an invitation code first.',
        KNOX_AUTH_OAUTH_NO_ELEVO_USER: 'Please sign in with your email and password once before using OAuth login.',
        KNOX_AUTH_OAUTH_FAILED: 'OAuth sign-in failed. Please try again.',
        KNOX_BAD_REQUEST: 'Unsupported OAuth provider.',
        KNOX_INTERNAL_ERROR: 'Server error. Please try again later.',
        unknown: 'Sign-in failed. Please try again.',
      },
    },
    loginForm: {
      email: 'Email Address',
      emailPlaceholder: 'name@company.com',
      change: 'Change',
      password: 'Password',
      passwordPlaceholder: 'Enter your password',
      continue: 'Continue',
      signingIn: 'Signing in...',
      signIn: 'Sign in',
      checkingEmail: 'Checking...',
      setPasswordTitle: 'Set Your Password',
      setPasswordSubtitle: 'Add a password to enable email login.',
      registerTitle: 'Create your account',
      registerSubtitle: 'Set up your name and password to get started.',
      name: 'Full Name',
      namePlaceholder: 'Your full name',
      createPassword: 'Create Password',
      passwordRules: '12+ characters with letters, numbers & symbols',
      confirmPassword: 'Confirm Password',
      confirmPasswordPlaceholder: 'Re-enter your password',
      signingUp: 'Creating account...',
      signUp: 'Create Account',
      settingPassword: 'Setting password...',
      setPassword: 'Set Password',
      forgotPassword: 'Forgot password?',
      verifyEmailTitle: 'Check Your Email',
      verifyEmailSentTo: "We've sent a verification code to",
      verifyEmailSubtitle: "We've sent a 6-digit verification code to your email address.",
      verifying: 'Verifying...',
      resendCode: 'Resend code',
      resendIn: 'Resend in {{seconds}}s',
    },
    forgotPassword: {
      title: 'Forgot Password',
      subtitle: 'Enter your email address and we\'ll send you a password reset link.',
      send: 'Send Reset Link',
      sending: 'Sending...',
      checkEmail: 'Check Your Email',
      checkEmailDescription: 'If that email is registered, you\'ll receive a password reset email.',
      backToLogin: 'Back to Login',
      resendIn: 'Resend in {{seconds}}s',
      resend: 'Resend email',
    },
    resetPassword: {
      title: 'Set New Password',
      verifying: 'Verifying link...',
      linkExpired: 'Link Expired',
      linkExpiredDescription: 'This password reset link has expired or been used. Please request a new one.',
      requestNewLink: 'Request a new reset link',
      for: 'Set new password for {{email}}',
      passwordMismatch: 'Passwords do not match',
      resetting: 'Resetting...',
      reset: 'Reset Password',
    },
    oauth: {
      continueWithGoogle: 'Continue with Google',
      continueWithGitHub: 'Continue with GitHub',
    },
    register: {
      noToken: 'No invitation token provided',
      expired: 'This invitation has expired',
      invalid: 'Invalid or expired invitation',
      failed: 'Registration failed',
      loginFailed: 'Login failed',
      invitationNotFound: 'Invitation Not Found',
      goToLogin: 'Go to Login',
      createYourTeam: 'Create Your Team',
      setupOrg: 'Set up your organization to get started',
      daysLicense: '{{days}} days license',
      upToMembers: 'Up to {{count}} members',
      checking: 'Checking...',
      fullName: 'Full Name',
      fullNamePlaceholder: 'Your full name',
      createPassword: 'Create a password',
      passwordRules: '12+ characters with letters, numbers & symbols',
      confirmPassword: 'Confirm Password',
      confirmPasswordPlaceholder: 'Re-enter your password',
      next: 'Next',
      orgName: 'Organization Name',
      orgNamePlaceholder: 'Your company name',
      orgSlug: 'Organization Slug',
      orgSlugPlaceholder: 'your-company',
      orgSlugHint: 'Used in your organization URL',
      back: 'Back',
      creating: 'Creating...',
      createOrg: 'Create Organization',
    },
    createRoom: {
      title: 'Create Room',
      description: 'Start a new Crew room.',
      nameLabel: 'Room name',
      namePlaceholder: 'e.g. prod-outage',
      descriptionLabel: 'Description',
      descriptionPlaceholder: "What's this room about?",
      cancel: 'Cancel',
      creating: 'Creating...',
      create: 'Create Room',
      failed: 'Failed to create room. Please try again.',
    },
    room: {
      jumpToTime: 'Jump to Time',
      jumpToTimeTime: 'Time',
      jumpToTimeDate: 'Date',
      jumpToTimePreset: 'Preset',
      jumpToTimeToday: 'Today',
      jumpToTimeYesterday: 'Yesterday',
      jumpToTimeBeginning: 'Beginning',
      jumpToTimeGo: 'Go',
      jumpToTimeError: 'Failed to resolve date',
      leaveRoom: 'Leave Room',
      pinnedMessages: 'Pinned Messages',
      noPinnedMessages: 'No pinned messages',
      confirmLeave: 'Are you sure you want to leave this room?',
      leaveFailed: 'Failed to leave room!',
      leaving: 'Leaving...',
      leave: 'Leave',
      inviteUsers: 'Invite Users',
      searchUsers: 'Search by name or ID...',
      noUsersFound: 'No users found',
      joined: 'Joined',
      messageDeleted: 'This message has been deleted',
      markAsRead: 'Mark as Read',
      invite: 'Invite',
      hideMembers: 'Hide Members',
      showMembers: 'Show Members',
      moreOptions: 'More Options',
    },
    error: {
      loginFailed: 'Login failed',
      registrationFailed: 'Registration failed',
      retrySetupFailed: 'Retry setup failed. Please try again.',
    },
    metrics: {
      title: 'Knox Canvas',
    },
    inbox: {
      title: 'Inbox',
      closeAriaLabel: 'Close inbox',
      filter: {
        open: 'Pending',
        resolved: 'Resolved',
        all: 'All',
      },
      empty: {
        openTitle: 'All Clear',
        openDescription: 'No pending items',
        otherTitle: 'No Records',
        otherDescription: 'No resolved items yet',
      },
      card: {
        resolved: 'Resolved',
        dismissed: 'Dismissed',
        details: 'Details',
        creatingRoom: 'Creating...',
        processing: 'Processing...',
      },
      action: {
        createRoom: 'Create Room',
        dismiss: 'Dismiss',
        accept: 'Accept',
        reject: 'Reject',
        markRead: 'Mark as Read',
        startOnboarding: 'Get Started',
        dismissOnboarding: 'Skip',
        connectors: 'Add Connectors',
        inviteMembers: 'Invite Members',
      },
      toast: {
        accepted: 'Accepted',
        dismissed: 'Dismissed',
        roomCreated: 'Room created',
      },
      onboarding: {
        addConnectors: 'Add Connectors',
        addConnectorsSummary: 'Connect your first server or cloud account so Knox can manage your infrastructure',
        runAudit: 'Run Baseline Audit',
        runAuditSummary: 'Perform a first audit on connected resources to understand your infrastructure',
        inviteMembers: 'Invite Team Members',
        inviteMembersSummary: 'Invite colleagues to join your organization and collaborate',
        createRoom: 'Create Collaboration Room',
        createRoomSummary: 'Create your first team room to start collaborating',
      },
    },
    settings: {
      title: 'Settings Hub',
      description: 'Manage your workspace and preferences.',
      closeAriaLabel: 'Close settings',
      nav: {
        account: 'Account',
        usage: 'Usage',
        appearance: 'Appearance',
        organization: 'Organization',
        members: 'Members',
        connectors: 'Connectors',
        agents: 'Knox Daemon',        security: 'Security',
        activity: 'Activity',
      },
      agents: {
        title: 'Knox Daemon',
        description: 'Monitor and manage deployed Knox Daemons',
        install: 'Install Knox Daemon',
        installBack: 'Back',
        choosePlatform: 'Choose the deployment platform',
        copyCommand: 'Copy',
        copyTooltip: 'Copy to clipboard',
        copied: 'Copied!',
        cancel: 'Cancel',
        verify: 'Verify installation',
        noAgents: 'No Knox Daemons registered yet',
        loading: 'Loading...',
        stepInstall: 'Install Knox Daemon',
        deleteTitle: 'Delete Knox Daemon',
        deleteConfirm: 'This will permanently remove "{{name}}" from the system. The daemon will also be disconnected if currently online.',
        deleteInputLabel: 'Type the display name to confirm',
        deleteAction: 'Delete',
        columns: {
          name: 'Display Name',
          uuid: 'UUID',
          type: 'Type',
          networks: 'Networks',
          status: 'Status',
          version: 'Version',
          lastSeen: 'Last Seen',
          actions: 'Actions',
        },
        status: {
          online: 'Online',
          offline: 'Offline',
          pending: 'Pending',
        },
        confirmStop: 'Stop Knox Daemon "{{name}}"? It will go offline.',
        confirmRestart: 'Restart Knox Daemon "{{name}}"? It will briefly go offline.',
        confirmUninstall: 'Uninstall Knox Daemon "{{name}}"? Type the display name to confirm.',
        platformLinux: {
          title: 'Linux Server',
          desc: 'CentOS / Ubuntu / Debian. Supports x86_64 and ARM64.',
        },
        platformK8s: {
          title: 'Kubernetes',
          desc: 'StatefulSet deployment, auto RBAC.',
        },
        commandsTitle: 'Knox Daemon Commands',
        commandsDesc: 'Copy these commands and run them on the target machine.',
        cmdVerify: 'Verify',
        cmdLogs: 'Logs',
        cmdRestart: 'Restart',
        cmdStop: 'Stop',
        cmdUpgrade: 'Upgrade',
        cmdUninstall: 'Uninstall',
        accessMode: 'Access Mode',
        readWrite: 'Read Write',
        readOnly: 'Read Only',
        readWriteDesc: 'Full control: create, update, delete workloads (Deployments, StatefulSets, CronJobs, etc.), exec into Pods, scale services, and manage Prometheus alert rules.',
        readOnlyDesc: 'Observability only: view workloads, logs, and events. No modifications allowed.',
      },
      profile: {
        title: 'Account',
        description: 'Manage your personal information.',
        displayName: 'Display Name',
        avatar: 'Avatar',
        uploadAvatar: 'Upload Avatar',
        changeAvatar: 'Change Avatar',
        removeAvatar: 'Remove Avatar',
        email: 'Email',
        saveChanges: 'Save Changes',
        saving: 'Saving...',
        loading: 'Loading...',
        loadError: 'Failed to load profile',
      },
      appearance: {
        title: 'Appearance',
        description: 'Customize how Knox looks and feels.',
        modeTitle: 'Appearance Mode',
        light: 'Light',
        dark: 'Dark',
        system: 'System',
        languageTitle: 'Interface Language',
        debugMode: 'Debug Mode',
        debugModeDescription: 'Show all agent work steps inline instead of hiding thread messages.',
      },
      organization: {
        title: 'Organization',
        description: 'View your organization details.',
        logo: 'Logo',
        name: 'Name',
        slug: 'Slug',
        slugCopy: 'Copy slug',
        id: 'Organization ID',
        role: 'Your Role',
        switchOrg: 'Switch Organization',
        loading: 'Loading...',
        loadError: 'Failed to load organization',
        notFound: 'No organization found.',
      },
      usage: {
        title: 'Usage',
        description: 'Monitor your credit balance, recharge history, and usage details.',
        creditsLow: 'Credits running low',
        creditsExhausted: 'Credits Exhausted',
        creditsExhaustedDesc: 'Your credit balance has been depleted. Recharge to continue using AI features.',
        requestRecharge: '+ Request Recharge',
        rechargeDialogTitle: 'Recharge Request',
        rechargeDialogDesc: 'Your credit balance is insufficient. Please provide a reason for the recharge request. An administrator will review and process it.',
        rechargeDialogReason: 'Reason',
        rechargeDialogPlaceholder: 'Describe why you need a recharge...',
        rechargeDialogCancel: 'Cancel',
        rechargeDialogSubmit: 'Submit',
        rechargeDialogSubmitting: 'Submitting...',
        rechargeDialogSuccess: 'Request submitted. An administrator will review it shortly.',
        viewDetails: 'View usage details →',
        currentBalance: 'Current Balance',
        credits: 'credits',
        rechargeHistory: 'Recharge History',
        records: '{{count}} records',
        noRecords: 'No records',
        pageSize: '{{count}} / page',
        recharge: 'Recharge',
        detailsTitle: 'Usage Details',
        detailsDesc: 'Credit consumption log.',
        startDate: 'Start date',
        endDate: 'End date',
        to: 'to',
        totalUsed: 'Total {{credits}} credits used',
        loading: 'Loading...',
        columns: {
          user: 'User',
          time: 'Time',
          quota: 'Credit',
        },
      },
      members: {
        title: 'Members',
        count: '{{count}} member',
        countPlural: '{{count}} members',
        inOrg: 'in this organization.',
        loading: 'Loading...',
        loadError: 'Failed to load members',
        empty: 'No members found',
        invite: 'Invite',
        searchPlaceholder: 'Search by name or email…',
        inviteTitle: 'Invite Team Members',
        inviteDescription: 'Share this code or link to invite others to join your organization.',
        joinCodeLabel: 'Join Code',
        inviteLinkLabel: 'Invitation Link',
        inviteCopy: 'Copy',
        inviteCopied: 'Copied!',
        inviteExpiryNote: 'This link expires in 7 days.',
        inviteExpired: 'This invite has expired.',
        inviteExpiresIn1Day: 'Expires in 1 day.',
        inviteExpiresInDays: 'Expires in {{days}} days.',
        inviteRegenerate: 'Regenerate',
        regenerateConfirmTitle: 'Regenerate Invite Code?',
        regenerateConfirmDescription: 'The current invite code will be invalidated. Anyone who has this code will no longer be able to use it to join.',
        regenerateCancel: 'Cancel',
        regenerateConfirm: 'Regenerate',
        inviteGenerateError: 'Failed to generate invite link.',
        memberLimitExceeded: 'Organization member limit reached.',
        quotaHint: 'Remove existing members or upgrade your plan.',
        you: '(you)',
        role: {
          owner: 'Owner',
          admin: 'Admin',
          member: 'Member',
          change: 'Change role',
        },
        status: {
          active: 'Active',
          disabled: 'Disabled',
        },
        pagination: {
          showing: '{{from}}-{{to}} of {{total}}',
          previous: 'Previous page',
          next: 'Next page',
        },
        actions: {
          disable: 'Disable',
          enable: 'Enable',
          remove: 'Remove',
        },
      },
      connectors: {
        title: 'Connections',
        loading: 'Loading...',
        emptyTitle: 'No connectors configured',
        emptyDescription: 'Connect external services to empower AI agents.',
        addConnector: 'Add connector',
        manageConnectors: 'Manage connectors',
        catalogTitle: 'Available Connectors',
        backToList: 'Back to list',
        searchPlaceholder: 'Search connectors...',
        categoryAll: 'All',
        categoryInfra: 'Infrastructure',
        categoryCollaboration: 'Collaboration',
        noResults: 'No connectors found',
        retry: 'Retry',
        bundle: 'bundle',
        team: 'team',
        personal: 'personal',
        statusActive: 'active',
        statusDisabled: 'disabled',
        configModal: {
          title: 'Add {{name}}',
          connectionName: 'Connection Name',
          connectionNamePlaceholder: 'e.g., Production AWS',
          shareWithTeam: 'Share with team',
          teamDescription: 'All organization members can use this connection',
          personalDescription: 'Personal: only you can use this connection',
          create: 'Create Connection',
          creating: 'Creating...',
          oauthHint: 'Click Authorize to grant Knox access to your account via the third-party service.',
          oauthAuthorize: 'Authorize',
          authorizing: 'Redirecting...',
        },
        oauthCallback: {
          loading: 'Completing authorization...',
          success: 'Authorization successful! Redirecting...',
          error: 'Authorization failed. Please try again.',
          back: 'Go Back',
        },
        detailModal: {
          credentials: 'Required credentials',
          credentialsHint: 'This connector requires authentication. Configuration fields will be shown during setup.',
          capabilities: 'Capabilities',
          configure: 'Configure Connection',
          aliyun: {
            resourceDiscovery: 'Resource Discovery',
            resourceDiscoveryDesc: 'Query ECS instances, disks, networks and other resources',
            remoteExecution: 'Remote Execution',
            remoteExecutionDesc: 'Execute commands on ECS instances via Cloud Assistant',
          },
          aws: {
            resourceDiscovery: 'Resource Discovery',
            resourceDiscoveryDesc: 'Query EC2 instances, S3, RDS and other AWS resources',
            remoteExecution: 'Remote Execution',
            remoteExecutionDesc: 'Execute commands on EC2 instances via SSM Run Command',
          },
        },
        editModal: {
          type: 'Type',
          status: 'Status',
          created: 'Created',
          lastUsed: 'Last used',
          connectionName: 'Connection Name',
          credentials: 'Credentials',
          bindAgent: 'Bind Agent',
          noAgent: 'Not bound',
          loadingCredentials: 'Loading credentials...',
          shareWithTeam: 'Share with team',
          teamDescription: 'All organization members can use this connection',
          personalDescription: 'Personal: only you can use this connection',
          save: 'Save Changes',
          saving: 'Saving...',
          delete: 'Delete Connection',
          deleting: 'Deleting...',
          confirmTitle: 'Delete this connection?',
          confirmMessage: 'Are you sure you want to delete "{{name}}"?',
          bundleWarning: 'This is a bundle connection — all related connections (discovery + execution) will be deleted.',
          cancel: 'Cancel',
          confirmDelete: 'Delete',
        },
        toggleStatus: '{{name}} — toggle status',
      },
      security: {
        title: 'Command Policy',
        description: 'Manage allowed and blocked commands. Unlisted commands require approval.',
        nav: 'Security',
        orgTab: 'Organization',
        orgTabDesc: 'Org-level command allow/block rules. All members can view, admins can edit.',
        myTab: 'My Authorized',
        myTabDesc: 'Commands you have permanently authorized. Removing means future executions require approval.',
        readonlyNotice: 'Only admins can edit organization policies.',
        columnCommand: 'Command',
        columnPolicy: 'Policy',
        columnAddedBy: 'Added by',
        columnAuthorized: 'Authorized',
        allow: 'Allow',
        block: 'Block',
        addPlaceholder: 'e.g. docker rm, systemctl stop',
        addCommand: '+ Add Command',
        empty: 'No policies configured.',
        searchPlaceholder: 'Filter commands...',
        noResults: 'No matching commands.',
        tip: '`{cmd}` matches all commands. `{sub}` matches only that subcommand. Subcommand rules take priority over base command rules.',
        myTip: 'Adding a command here skips future approval prompts. Removing it means the next execution will require approval. Org-level policies always take priority.',
        addSuccess: 'Command added',
        addError: 'Failed to add command',
        removeSuccess: 'Command removed',
        removeError: 'Failed to remove command',
      },
      activity: {
        title: 'Activity Log',
        subtitle: 'Command executions and file operations across your organization',
        empty: 'No activity yet',
        emptyHint: 'Activity will appear here when commands are executed via connectors',
        error: 'Failed to load activity log',
        retry: 'Retry',
        filter: {
          all: 'All',
          tool: 'Tool',
          status: 'Status',
          allTools: 'All Tools',
          allStatuses: 'All Statuses',
          allTime: 'All Time',
          lastHour: 'Last Hour',
          last24h: 'Last 24h',
          last7d: 'Last 7 Days',
          last30d: 'Last 30 Days',
          custom: 'Custom Range',
          searchPlaceholder: 'Search commands, hosts...',
        },
        col: {
          time: 'Time',
          action: 'Action',
          host: 'Host',
          channel: 'Channel',
          status: 'Status',
          duration: 'Duration',
        },
        detail: {
          user: 'User',
          tool: 'Tool',
          channel: 'Channel',
          host: 'Host',
          exitCode: 'Exit Code',
          duration: 'Duration',
          command: 'Command',
          filePath: 'File Path',
          output: 'Output',
          outputTruncated: '(truncated)',
          error: 'Error',
        },
        batch: {
          tooltipSingle: 'Single execution',
          tooltipBatch: 'Batch operation',
          filterLabel: 'Batch',
        },
        pagination: {
          showing: 'Showing {{from}}-{{to}} of {{total}} records',
          total: '{{total}} records',
          prev: 'Previous',
          next: 'Next',
        },
      },
    },
    orgBinding: {
      title: 'Set Up Your Organization',
      subtitle: 'Create a new organization or join an existing one to get started.',
      createLabel: 'Create Organization',
      joinLabel: 'Join Organization',
    },
    joinCode: {
      title: 'Join an Organization',
      subtitle: 'Enter the invitation code shared by your team admin.',
      codeLabel: 'Invitation Code',
      placeholder: 'Enter your code',
      submit: 'Continue',
      invalidOrExpired: 'This code is invalid or has expired.',
      back: '← Back to options',
      noCode: "Don't have a code?",
    },
    inviteCode: {
      title: 'Create Your Organization',
      subtitle: 'Enter the invitation code and set up your organization.',
      codeLabel: 'Invitation Code',
      placeholder: 'Enter your code',
      orgNameLabel: 'Organization Name',
      orgNamePlaceholder: 'e.g. Acme Corp',
      submit: 'Create Organization',
      invalidOrExpired: 'This code is invalid or has expired.',
      back: '← Back to options',
      noCode: "Don't have a code?",
    },
    accessApplication: {
      trigger: 'Request Access',
      title: 'Request Access',
      subtitle: "Fill in your details and we'll review your application.",
      workEmail: 'Work Email',
      workEmailPlaceholder: 'you@company.com',
      fullName: 'Full Name',
      fullNamePlaceholder: 'Your name',
      companyName: 'Company or Team',
      companyNamePlaceholder: 'Your company or team name',
      role: 'Role',
      rolePlaceholder: 'Select your role',
      roleOptions: {
        itOperations: 'IT Operations',
        platformEngineering: 'Platform Engineering',
        sre: 'SRE',
        infrastructure: 'Infrastructure',
        engineeringManagement: 'Engineering Management',
        other: 'Other',
      },
      problem: 'What are you trying to solve with Knox?',
      problemPlaceholder: 'Tell us about your infrastructure challenges or operational problems.',
      teamSize: 'Team Size (optional)',
      teamSizePlaceholder: 'Select team size',
      submit: 'Submit Application',
      submitting: 'Submitting...',
      successTitle: 'Application Submitted',
      successMessage: "We'll review your request and email you an invite code if approved.",
      checkInbox: 'Check your inbox within 24 hours.',
      rateLimited: 'Too many requests. Please wait a moment and try again.',
      genericError: 'Something went wrong. Please try again.',
      required: 'This field is required.',
      invalidEmail: 'Enter a valid work email.',
      problemMinLength: 'Must be at least 20 characters.',
      close: 'Close',
    },
    invite: {
      loading: 'Loading invitation...',
      joining: 'Joining organization...',
      joinOrg: 'Join {{orgName}}',
      invitedBy: '{{name}} has invited you to join',
      invitedToJoin: "You've been invited to join",
      asRole: 'You will join as {{role}}',
      expired: 'This invitation has expired.',
      invalid: 'This invitation link is invalid.',
      used: 'This invitation has already been used.',
      error: 'Failed to load invitation details.',
      acceptError: 'Failed to accept invitation. Please try again.',
      loginToJoin: 'Log in to accept this invitation',
      email: 'Email',
      password: 'Password',
      name: 'Full Name',
      login: 'Log In & Join',
      noAccount: "Don't have an account?",
      createAccount: 'Create Account',
      haveAccount: 'Already have an account?',
      signIn: 'Sign In',
      register: 'Create Account & Join',
      registering: 'Creating account...',
      loggingIn: 'Logging in...',
      confirmJoin: 'Join Organization',
      backToLogin: 'Back to login',
      passwordRules: '12+ characters with letters, numbers & symbols',
      confirmPassword: 'Confirm Password',
    },
    onboarding: {
      welcomeTitle: 'Welcome to Knox',
      welcomeSubtitle: 'Your AI-native infrastructure operations team is ready.',
      ctaButton: 'Get Started',
      ctaLoading: 'Setting up...',
      progressTitle: 'Onboarding',
      agentCaptain: 'Orchestrates tasks and coordinates the team',
      agentCollector: 'Gathers infrastructure data from your servers',
      agentSupervisor: 'Validates data completeness and consistency',
      agentArchitect: 'Analyzes architecture and traces dependencies',
      agentChronicler: 'Records findings to the knowledge graph',
      agentCoordinator: 'Manages workflows and schedules',
      step1Title: 'Meet Your AI Team',
      step2Title: 'Connect a Server',
      step2Desc: 'Tell Captain about your server in the chat panel.',
      step3Title: 'Generate Discovery Report',
      step3Desc: 'Your AI team will analyze your infrastructure.',
      step3Analyzing: 'AI team is analyzing your infrastructure...',
      step3Complete: 'Report ready',
      viewReport: 'View Full Report',
      completed: 'Onboarding Complete!',
      skip: 'Skip onboarding',
      continueGuide: 'Continue',
    },
    structured: {
      tool: {
        running: 'Running...',
        completed: 'Completed',
        error: 'Error',
        showOutput: 'Show output',
        hideOutput: 'Hide output',
        loadingOutput: 'Loading output...',
        retry: 'Retry',
      },
      error: {
        title: 'Error',
      },
      askHuman: {
        waitingForInput: 'Waiting for input...',
        submitted: 'Response submitted',
        expired: 'Timed out',
        answer: 'Answer',
        question: 'Question',
        other: 'Other',
        typeYourAnswer: 'Type your answer...',
        submitting: 'Submitting...',
        submit: 'Submit',
        selectPlaceholder: 'Select an option...',
      },
      approval: {
        approved: 'Approved',
        rejected: 'Rejected',
        expired: 'Approval timed out',
        expiredHint: 'Command was not executed',
        alwaysAllow: 'Always allow',
        needsYourAuth: 'Needs your authorization',
        waitingFor: 'Waiting for {{name}} to approve',
        processing: 'Processing...',
        reject: 'Reject',
        allowOnce: 'Allow',
        approvalFailed: 'Approval failed',
        rejectionFailed: 'Rejection failed',
        waitingForApproval: 'Waiting for approval...',
        alwaysAllowScope: 'Always allow will authorize',
        complexCommandNoAlwaysAllow: 'This command uses complex shell features and cannot be auto-authorized. Each execution requires manual approval.',
      },
      threadAck: {
        processing: 'Processing...',
        completed: 'Completed',
        error: 'Error occurred',
      },
      systemNotice: {
        sessionCleared: 'Session cleared. Starting fresh.',
        sessionStopped: 'Processing stopped.',
        sessionStoppedIdle: 'Nothing is currently running.',
        processingInterrupted: 'Processing interrupted.',
        allStopped: 'Stopped: {{names}}',
        allStoppedIdle: 'Nothing is currently running.',
        allCleared: 'All sessions cleared.',
        personaCleared: '{{name}} session cleared.',
      },
      botControl: {
        stopTooltip: 'Stop all running tasks',
        clearSession: 'Clear Session',
        clearAll: 'Clear All',
      },
      nameSeparator: ', ',
      search: {
        scopeThisRoom: 'This Room',
        scopeAllRooms: 'All Rooms',
        searchMessages: 'Search Messages',
        searchInRoom: 'Search in this room',
        searchAllRooms: 'Search across all rooms',
        noResults: 'No results for <b>{{term}}</b>',
        noResultsHint: 'Try different keywords or check the spelling',
        thread: 'Thread',
        replyTo: 'Reply',
      },
      profile: {
        roleAdmin: 'Admin',
        roleModerator: 'Moderator',
        roleMember: 'Member',
        mention: 'Mention',
        stop: 'Stop',
        clear: 'Clear',
        aiBadge: 'AI',
        confirmClear: 'Reset {{name}}\'s context? All conversation history will be lost.',
        confirmStop: 'Stop {{name}}? It will be cancelled if currently running.',
        confirmBtn: 'Confirm',
        cancelBtn: 'Cancel',
      },
      typing: {
        thinking: 'Thinking...',
        oneTyping: '<bold>{{name}}</bold> is typing...',
        twoTyping: '<bold>{{name1}}</bold> and <bold>{{name2}}</bold> are typing...',
        manyTyping: '<bold>{{name}}</bold> and {{count}} others are typing...',
      },
      timeline: {
        newMessages: 'New messages',
        jumpToLatest: 'Jump to Latest',
        noMessagesYet: 'No messages yet',
        suggestions: {
          title: 'Knox Team Ready',
          subtitle: 'Select a quick start, or type your own command',
          baseline: {
            title: 'Baseline Report',
            desc: 'Get a full picture of the current system',
            prompt: 'Run a discovery report on my infrastructure',
          },
          troubleshoot: {
            title: 'Troubleshoot an Issue',
            desc: 'Investigate and resolve production problems',
            prompt: 'Help me troubleshoot an issue:',
          },
          change: {
            title: 'Execute a Change',
            desc: 'Run operations and configuration updates',
            prompt: 'Help me execute a change:',
          },
          capabilities: {
            title: 'What Can You Do?',
            desc: 'Learn about the team\'s capabilities',
            prompt: 'Introduce the team\'s capabilities. What can you help me with?',
          },
        },
        inboxWelcome: {
          greetingMorning: 'Good morning, {{name}}',
          greetingAfternoon: 'Good afternoon, {{name}}',
          greetingEvening: 'Good evening, {{name}}',
          subtitle: 'Your AI SRE Team — discovering, monitoring, troubleshooting, and operations',
          alertSummary: {
            label: 'Alert Summary',
            prompt: 'Summarize current alerts',
          },
          healthCheck: {
            label: 'Health Check',
            prompt: 'Run a system health check',
          },
          troubleshoot: {
            label: 'Troubleshoot',
            prompt: 'Help me troubleshoot an issue:',
          },
          capabilities: {
            label: 'What Can You Do?',
            prompt: 'What can you help me with?',
          },
        },
      },
    },
  },
};

const KNOX_RESOURCES_ZH: Record<string, Record<string, unknown>> = {
  'knox-aiops': {
    nav: {
      brand: 'Knox AI-Native AIOps',
      triageInbox: '分诊收件箱',
      members: '成员',
      selectRoom: '选择一个房间开始',
      settings: '设置',
      signOut: '退出登录',
      support: '帮助与支持',
      lightMode: '浅色模式',
      darkMode: '深色模式',
      expandSidebar: '展开侧边栏',
      collapseSidebar: '收起侧边栏',
      newRoom: '新建房间',
      inbox: '收件箱',
      inboxPending: '待处理事项',
      searchRooms: '搜索房间...',
      searchRoomsAria: '搜索房间',
      clearSearch: '清除搜索',
      rooms: '房间',
      noRoomsFound: '未找到房间',
      noRoomsYet: '暂无房间',
      createFirstRoom: '创建你的第一个房间',
      showingRoomsOf: '显示 {{shown}} / 共 {{total}} 个房间 — 使用搜索查找更多',
      agents: 'Knox Daemon',
    },
    login: {
      signIn: '登录 Knox',
      subtitle: '你的 AI 原生 IT 运维团队已就绪',
      invitationCode: '有邀请码？',
      invitationCodeLabel: '邀请码',
      invitationCodePlaceholder: '输入邀请码',
      go: '前往',
      copyright: '© 2026 Knox',
      termsOfService: '服务条款',
      privacyPolicy: '隐私政策',
    },
    loginForm: {
      email: '邮箱地址',
      emailPlaceholder: 'name@company.com',
      change: '更换',
      password: '密码',
      passwordPlaceholder: '输入密码',
      continue: '继续',
      signingIn: '登录中...',
      signIn: '登录',
      checkingEmail: '检查中...',
      setPasswordTitle: '设置密码',
      setPasswordSubtitle: '添加密码以启用邮箱登录。',
      registerTitle: '创建你的账号',
      registerSubtitle: '设置你的姓名和密码以开始使用。',
      name: '姓名',
      namePlaceholder: '你的姓名',
      createPassword: '创建密码',
      passwordRules: '至少12个字符，包含字母、数字和符号',
      confirmPassword: '确认密码',
      confirmPasswordPlaceholder: '再次输入密码',
      signingUp: '创建中...',
      signUp: '创建账号',
      settingPassword: '设置密码中...',
      setPassword: '设置密码',
      forgotPassword: '忘记密码？',
      verifyEmailTitle: '请查看您的邮箱',
      verifyEmailSentTo: '我们已向以下邮箱发送验证码',
      verifyEmailSubtitle: '我们已向您的邮箱发送了 6 位验证码。',
      verifying: '正在验证...',
      resendCode: '重新发送验证码',
      resendIn: '{{seconds}}秒后可重新发送',
    },
    forgotPassword: {
      title: '忘记密码',
      subtitle: '输入您的邮箱地址，我们将发送密码重置链接。',
      send: '发送重置链接',
      sending: '发送中...',
      checkEmail: '请查看您的邮箱',
      checkEmailDescription: '如果该邮箱已注册，您将收到重置密码的邮件。',
      backToLogin: '返回登录',
      resendIn: '{{seconds}}秒后可重新发送',
      resend: '重新发送邮件',
    },
    resetPassword: {
      title: '设置新密码',
      verifying: '正在验证链接...',
      linkExpired: '链接已失效',
      linkExpiredDescription: '此重置密码链接已过期或已被使用，请重新申请。',
      requestNewLink: '重新申请重置链接',
      for: '为 {{email}} 设置新密码',
      passwordMismatch: '两次输入的密码不一致',
      resetting: '重置中...',
      reset: '重置密码',
    },
    oauth: {
      continueWithGoogle: '使用 Google 继续',
      continueWithGitHub: '使用 GitHub 继续',
    },
    register: {
      noToken: '未提供邀请令牌',
      expired: '此邀请已过期',
      invalid: '邀请无效或已过期',
      failed: '注册失败',
      loginFailed: '登录失败',
      invitationNotFound: '邀请不存在',
      goToLogin: '前往登录',
      createYourTeam: '创建你的团队',
      setupOrg: '设置你的组织以开始使用',
      daysLicense: '{{days}} 天许可',
      upToMembers: '最多 {{count}} 位成员',
      checking: '检查中...',
      fullName: '姓名',
      fullNamePlaceholder: '你的姓名',
      createPassword: '创建密码',
      passwordRules: '至少12个字符，包含字母、数字和符号',
      confirmPassword: '确认密码',
      confirmPasswordPlaceholder: '再次输入密码',
      next: '下一步',
      orgName: '组织名称',
      orgNamePlaceholder: '你的公司名称',
      orgSlug: '组织标识',
      orgSlugPlaceholder: 'your-company',
      orgSlugHint: '用于组织 URL',
      back: '返回',
      creating: '创建中...',
      createOrg: '创建组织',
    },
    createRoom: {
      title: '创建房间',
      description: '创建一个新的 Crew 房间。',
      nameLabel: '房间名称',
      namePlaceholder: '例如 prod-outage',
      descriptionLabel: '描述',
      descriptionPlaceholder: '这个房间是做什么的？',
      cancel: '取消',
      creating: '创建中...',
      create: '创建房间',
      failed: '创建房间失败，请重试。',
    },
    room: {
      jumpToTime: '跳转到时间点',
      jumpToTimeTime: '时间',
      jumpToTimeDate: '日期',
      jumpToTimePreset: '快捷选择',
      jumpToTimeToday: '今天',
      jumpToTimeYesterday: '昨天',
      jumpToTimeBeginning: '最早',
      jumpToTimeGo: '跳转',
      jumpToTimeError: '无法解析日期',
      leaveRoom: '离开房间',
      pinnedMessages: '置顶消息',
      noPinnedMessages: '暂无置顶消息',
      confirmLeave: '确定要离开这个房间吗？',
      leaveFailed: '离开房间失败！',
      leaving: '离开中...',
      leave: '离开',
      inviteUsers: '邀请用户',
      searchUsers: '按姓名或 ID 搜索...',
      noUsersFound: '未找到用户',
      joined: '已加入',
      messageDeleted: '此消息已被删除',
      markAsRead: '标记为已读',
      invite: '邀请',
      hideMembers: '隐藏成员',
      showMembers: '显示成员',
      moreOptions: '更多选项',
    },
    error: {
      loginFailed: '登录失败',
      registrationFailed: '注册失败',
      retrySetupFailed: '重试设置失败，请重试。',
    },
    metrics: {
      title: '指标面板',
    },
    inbox: {
      title: '收件箱',
      closeAriaLabel: '关闭收件箱',
      filter: {
        open: '待处理',
        resolved: '已处理',
        all: '全部',
      },
      empty: {
        openTitle: '一切就绪',
        openDescription: '没有待处理的事项',
        otherTitle: '暂无记录',
        otherDescription: '还没有已处理的事项',
      },
      card: {
        resolved: '已处理',
        dismissed: '已忽略',
        details: '详情',
        creatingRoom: '创建中...',
        processing: '处理中...',
      },
      action: {
        createRoom: '创建房间',
        dismiss: '忽略',
        accept: '接受',
        reject: '拒绝',
        markRead: '已读',
        startOnboarding: '开始',
        dismissOnboarding: '跳过',
        connectors: '接入连接器',
        inviteMembers: '邀请成员',
      },
      toast: {
        accepted: '已接受邀请',
        dismissed: '已拒绝邀请',
        roomCreated: '房间已创建',
      },
      onboarding: {
        addConnectors: '添加连接器',
        addConnectorsSummary: '连接你的第一台服务器或云账号，让 Knox 团队可以帮你管理基础设施',
        runAudit: '执行摸底报告',
        runAuditSummary: '对已连接的资源执行首次摸底，了解你的基础设施全貌',
        inviteMembers: '邀请团队成员',
        inviteMembersSummary: '邀请同事加入组织，一起协作管理基础设施',
        createRoom: '创建协作房间',
        createRoomSummary: '创建第一个团队协作房间，开始团队沟通',
      },
    },
    settings: {
      title: '设置中心',
      description: '管理工作空间和个人偏好。',
      closeAriaLabel: '关闭设置',
      nav: {
        account: '账号',
        usage: '使用情况',
        appearance: '外观',
        organization: '组织',
        members: '成员',
        connectors: '连接器',
        agents: 'Knox Daemon',        security: '安全策略',
        activity: '活动日志',
      },
      agents: {
        title: 'Knox Daemon 管理',
        description: '监控和管理已部署的 Knox Daemon',
        install: '安装 Knox Daemon',
        installBack: '返回',
        choosePlatform: '选择部署平台',
        copyCommand: '复制',
        copyTooltip: '复制到剪贴板',
        copied: '已复制!',
        cancel: '取消',
        verify: '验证安装',
        noAgents: '暂无已注册的 Knox Daemon',
        loading: '加载中...',
        stepInstall: '安装 Knox Daemon',
        deleteTitle: '删除 Knox Daemon',
        deleteConfirm: '此操作将永久移除 "{{name}}"。如果该 Daemon 当前在线，连接也将被断开。',
        deleteInputLabel: '输入显示名称以确认',
        deleteAction: '删除',
        columns: {
          name: '显示名称',
          uuid: 'UUID',
          type: '类型',
          networks: '网络',
          status: '状态',
          version: '版本',
          lastSeen: '最后心跳',
          actions: '操作',
        },
        status: {
          online: '在线',
          offline: '离线',
          pending: '等待中',
        },
        confirmStop: '确认停止 Knox Daemon "{{name}}"？它将离线。',
        confirmRestart: '确认重启 Knox Daemon "{{name}}"？它会短暂离线。',
        confirmUninstall: '确认卸载 Knox Daemon "{{name}}"？请输入显示名称以确认。',
        platformLinux: {
          title: 'Linux 服务器',
          desc: 'CentOS / Ubuntu / Debian，支持 x86_64 和 ARM64。',
        },
        platformK8s: {
          title: 'Kubernetes',
          desc: 'StatefulSet 部署，自动 RBAC。',
        },
        commandsTitle: 'Knox Daemon 常用命令',
        commandsDesc: '复制以下命令，在目标机器上执行。',
        cmdVerify: '验证状态',
        cmdLogs: '查看日志',
        cmdRestart: '重启',
        cmdStop: '停止',
        cmdUpgrade: '升级版本',
        cmdUninstall: '卸载',
        accessMode: '访问模式',
        readWrite: '读写',
        readOnly: '只读',
        readWriteDesc: '完整控制：可创建、更新、删除工作负载（Deployment、StatefulSet、CronJob 等），进入 Pod 执行命令，扩缩容服务，以及管理 Prometheus 告警规则。',
        readOnlyDesc: '仅可观测：查看工作负载、日志和事件，不允许任何修改操作。',
      },
      profile: {
        title: '账号',
        description: '管理你的个人信息。',
        displayName: '显示名称',
        avatar: '头像',
        uploadAvatar: '上传头像',
        changeAvatar: '更换头像',
        removeAvatar: '移除头像',
        email: '邮箱',
        saveChanges: '保存更改',
        saving: '保存中...',
        loading: '加载中...',
        loadError: '加载个人信息失败',
      },
      appearance: {
        title: '外观',
        description: '自定义 Knox 的外观和体验。',
        modeTitle: '外观模式',
        light: '浅色',
        dark: '深色',
        system: '跟随系统',
        languageTitle: '界面语言',
        debugMode: '调试模式',
        debugModeDescription: '显示所有 Agent 工作步骤，不隐藏线程消息。',
      },
      organization: {
        title: '组织',
        description: '查看组织详情。',
        logo: 'Logo',
        name: '名称',
        slug: '标识符',
        slugCopy: '复制标识符',
        id: '组织 ID',
        role: '你的角色',
        switchOrg: '切换组织',
        loading: '加载中...',
        loadError: '加载组织信息失败',
        notFound: '未找到组织。',
      },
      usage: {
        title: '使用情况',
        description: '监控积分、充值记录和使用明细。',
        creditsLow: '积分即将不足',
        creditsExhausted: '积分已耗尽',
        creditsExhaustedDesc: '积分已耗尽，请充值以继续使用 AI 功能。',
        requestRecharge: '+ 申请充值',
        rechargeDialogTitle: '申请充值',
        rechargeDialogDesc: '积分不足，请填写充值申请原因，管理员将审核并处理。',
        rechargeDialogReason: '原因',
        rechargeDialogPlaceholder: '请描述您需要充值的原因...',
        rechargeDialogCancel: '取消',
        rechargeDialogSubmit: '提交',
        rechargeDialogSubmitting: '提交中...',
        rechargeDialogSuccess: '申请已提交，管理员将尽快审核。',
        viewDetails: '查看使用明细 →',
        currentBalance: '当前积分',
        credits: '积分',
        rechargeHistory: '充值记录',
        records: '{{count}} 条记录',
        noRecords: '暂无记录',
        pageSize: '{{count}} 条/页',
        recharge: '充值',
        detailsTitle: '使用明细',
        detailsDesc: '积分消耗记录。',
        startDate: '开始日期',
        endDate: '结束日期',
        to: '至',
        totalUsed: '共消耗 {{credits}} 积分',
        loading: '加载中...',
        columns: {
          user: '用户',
          time: '时间',
          quota: '积分',
        },
      },
      members: {
        title: '成员',
        count: '{{count}} 位成员',
        countPlural: '{{count}} 位成员',
        inOrg: '。',
        loading: '加载中...',
        loadError: '加载成员列表失败',
        empty: '暂无成员',
        invite: '邀请',
        searchPlaceholder: '按姓名或邮箱搜索…',
        inviteTitle: '邀请团队成员',
        inviteDescription: '分享此邀请码或链接以邀请他人加入你的组织。',
        joinCodeLabel: '邀请码',
        inviteLinkLabel: '邀请链接',
        inviteCopy: '复制',
        inviteCopied: '已复制！',
        inviteExpiryNote: '此链接 7 天内有效。',
        inviteExpired: '此邀请已过期。',
        inviteExpiresIn1Day: '1 天后过期。',
        inviteExpiresInDays: '{{days}} 天后过期。',
        inviteRegenerate: '重新生成',
        regenerateConfirmTitle: '重新生成邀请码？',
        regenerateConfirmDescription: '当前邀请码将失效，已分享出去的链接将无法使用。',
        regenerateCancel: '取消',
        regenerateConfirm: '确认重新生成',
        inviteGenerateError: '生成邀请链接失败。',
        memberLimitExceeded: '已达到组织成员上限。',
        quotaHint: '请移除现有成员或升级套餐。',
        you: '（你）',
        role: {
          owner: '所有者',
          admin: '管理员',
          member: '成员',
          change: '更改角色',
        },
        status: {
          active: '正常',
          disabled: '已禁用',
        },
        pagination: {
          showing: '第 {{from}}-{{to}} 条，共 {{total}} 条',
          previous: '上一页',
          next: '下一页',
        },
        actions: {
          disable: '禁用',
          enable: '启用',
          remove: '移除',
        },
      },
      connectors: {
        title: '连接',
        loading: '加载中...',
        emptyTitle: '暂无连接器',
        emptyDescription: '连接外部服务，赋能 AI Agent。',
        addConnector: '添加连接器',
        manageConnectors: '管理连接器',
        catalogTitle: '可用连接器',
        backToList: '返回列表',
        searchPlaceholder: '搜索连接器...',
        categoryAll: '全部',
        categoryInfra: '基础设施',
        categoryCollaboration: '协作办公',
        noResults: '未找到连接器',
        retry: '重试',
        bundle: '组合',
        team: '团队',
        personal: '个人',
        statusActive: '正常',
        statusDisabled: '已禁用',
        configModal: {
          title: '添加 {{name}}',
          connectionName: '连接名称',
          connectionNamePlaceholder: '例如：生产环境 AWS',
          shareWithTeam: '共享给团队',
          teamDescription: '组织内所有成员都可以使用此连接',
          personalDescription: '个人连接：仅你可以使用',
          create: '创建连接',
          creating: '创建中...',
          oauthHint: '点击授权按钮后，将跳转到第三方服务进行授权。',
          oauthAuthorize: '授权',
          authorizing: '正在跳转...',
        },
        oauthCallback: {
          loading: '正在完成授权...',
          success: '授权成功！即将跳转...',
          error: '授权失败，请重试。',
          back: '返回',
        },
        detailModal: {
          credentials: '需要凭证',
          credentialsHint: '此连接器需要身份验证。配置字段将在设置过程中显示。',
          capabilities: '包含能力',
          configure: '配置连接',
          aliyun: {
            resourceDiscovery: '资源发现',
            resourceDiscoveryDesc: '查询 ECS 实例、磁盘、网络等阿里云资源',
            remoteExecution: '远程执行',
            remoteExecutionDesc: '通过云助手在 ECS 实例上执行命令',
          },
          aws: {
            resourceDiscovery: '资源发现',
            resourceDiscoveryDesc: '查询 EC2 实例、S3、RDS 等 AWS 资源',
            remoteExecution: '远程执行',
            remoteExecutionDesc: '通过 SSM Run Command 在 EC2 实例上执行命令',
          },
        },
        editModal: {
          type: '类型',
          status: '状态',
          created: '创建时间',
          lastUsed: '最近使用',
          connectionName: '连接名称',
          credentials: '凭证',
          bindAgent: '绑定 Agent',
          noAgent: '未绑定',
          loadingCredentials: '加载凭证中...',
          shareWithTeam: '共享给团队',
          teamDescription: '组织内所有成员都可以使用此连接',
          personalDescription: '个人连接：仅你可以使用',
          save: '保存更改',
          saving: '保存中...',
          delete: '删除连接',
          deleting: '删除中...',
          confirmTitle: '确定删除此连接？',
          confirmMessage: '确定要删除 "{{name}}" 吗？',
          bundleWarning: '这是组合连接 — 所有相关连接（发现 + 执行）都将被删除。',
          cancel: '取消',
          confirmDelete: '删除',
        },
        toggleStatus: '{{name}} — 切换状态',
      },
      security: {
        title: '命令策略',
        description: '管理允许和阻止的命令。未列出的命令需要审批。',
        nav: '安全策略',
        orgTab: '组织策略',
        orgTabDesc: '组织级命令允许/阻止规则。所有成员可查看，管理员可编辑。',
        myTab: '我的授权',
        myTabDesc: '你已永久授权的命令。移除后下次执行需要审批。',
        readonlyNotice: '仅管理员可编辑组织策略。',
        columnCommand: '命令',
        columnPolicy: '策略',
        columnAddedBy: '添加人',
        columnAuthorized: '授权时间',
        allow: '允许',
        block: '阻止',
        addPlaceholder: '例如 docker rm, systemctl stop',
        addCommand: '+ 添加命令',
        empty: '暂无策略配置。',
        searchPlaceholder: '过滤命令...',
        noResults: '没有匹配的命令。',
        tip: '`{cmd}` 匹配所有命令。`{sub}` 仅匹配该子命令。子命令规则优先于基础命令规则。',
        myTip: '在此添加命令可跳过审批提示。移除后下次执行需要审批。组织级策略始终优先。',
        addSuccess: '命令已添加',
        addError: '添加命令失败',
        removeSuccess: '命令已移除',
        removeError: '移除命令失败',
      },
      activity: {
        title: '活动日志',
        subtitle: '组织内的命令执行和文件操作记录',
        empty: '暂无活动记录',
        emptyHint: '通过连接器执行命令后，活动记录将显示在这里',
        error: '加载活动日志失败',
        retry: '重试',
        filter: {
          all: '全部',
          tool: '工具',
          status: '状态',
          allTools: '所有工具',
          allStatuses: '所有状态',
          allTime: '全部时间',
          lastHour: '最近 1 小时',
          last24h: '最近 24 小时',
          last7d: '最近 7 天',
          last30d: '最近 30 天',
          custom: '自定义范围',
          searchPlaceholder: '搜索命令、主机...',
        },
        col: {
          time: '时间',
          action: '操作',
          host: '主机',
          channel: '通道',
          status: '状态',
          duration: '耗时',
        },
        detail: {
          user: '用户',
          tool: '工具',
          channel: '通道',
          host: '主机',
          exitCode: '退出码',
          duration: '耗时',
          command: '命令',
          filePath: '文件路径',
          output: '输出',
          outputTruncated: '(已截断)',
          error: '错误',
        },
        batch: {
          tooltipSingle: '单次执行',
          tooltipBatch: '批量操作',
          filterLabel: '批次',
        },
        pagination: {
          showing: '显示 {{from}}-{{to}} / 共 {{total}} 条',
          total: '共 {{total}} 条',
          prev: '上一页',
          next: '下一页',
        },
      },
    },
    orgBinding: {
      title: '设置你的组织',
      subtitle: '创建新组织或加入已有组织以开始使用。',
      createLabel: '创建组织',
      joinLabel: '加入组织',
    },
    joinCode: {
      title: '加入组织',
      subtitle: '输入团队管理员分享的邀请码。',
      codeLabel: '邀请码',
      placeholder: '输入邀请码',
      submit: '继续',
      invalidOrExpired: '邀请码无效或已过期。',
      back: '← 返回选择',
      noCode: '没有邀请码？',
    },
    inviteCode: {
      title: '创建组织',
      subtitle: '输入邀请码并设置你的组织信息。',
      codeLabel: '邀请码',
      placeholder: '输入邀请码',
      orgNameLabel: '组织名称',
      orgNamePlaceholder: '例如：XX科技',
      submit: '创建组织',
      invalidOrExpired: '邀请码无效或已过期。',
      back: '← 返回选择',
      noCode: '没有邀请码？',
    },
    accessApplication: {
      trigger: '申请访问',
      title: '申请访问',
      subtitle: '填写信息，我们会审核你的申请。',
      workEmail: '工作邮箱',
      workEmailPlaceholder: 'you@company.com',
      fullName: '姓名',
      fullNamePlaceholder: '你的姓名',
      companyName: '公司或团队',
      companyNamePlaceholder: '你的公司或团队名称',
      role: '职位',
      rolePlaceholder: '选择你的职位',
      roleOptions: {
        itOperations: 'IT 运维',
        platformEngineering: '平台工程',
        sre: 'SRE',
        infrastructure: '基础设施',
        engineeringManagement: '工程管理',
        other: '其他',
      },
      problem: '你希望用 Knox 解决什么问题？',
      problemPlaceholder: '告诉我们你面临的基础设施挑战或运维问题。',
      teamSize: '团队规模（选填）',
      teamSizePlaceholder: '选择团队规模',
      submit: '提交申请',
      submitting: '提交中...',
      successTitle: '申请已提交',
      successMessage: '我们会审核你的申请，通过后会将邀请码发送到你的邮箱。',
      checkInbox: '请在 24 小时内查看邮箱。',
      rateLimited: '请求过于频繁，请稍后再试。',
      genericError: '出了点问题，请重试。',
      required: '此项为必填。',
      invalidEmail: '请输入有效的工作邮箱。',
      problemMinLength: '至少需要 20 个字符。',
      close: '关闭',
    },
    invite: {
      loading: '加载邀请信息...',
      joining: '正在加入组织...',
      joinOrg: '加入 {{orgName}}',
      invitedBy: '{{name}} 邀请你加入',
      invitedToJoin: '你被邀请加入',
      asRole: '你将以 {{role}} 身份加入',
      expired: '此邀请已过期。',
      invalid: '此邀请链接无效。',
      used: '此邀请已被使用。',
      error: '加载邀请详情失败。',
      acceptError: '接受邀请失败，请重试。',
      loginToJoin: '登录以接受此邀请',
      email: '邮箱',
      password: '密码',
      name: '姓名',
      login: '登录并加入',
      noAccount: '还没有账号？',
      createAccount: '创建账号',
      haveAccount: '已有账号？',
      signIn: '登录',
      register: '注册并加入',
      registering: '正在创建账号...',
      loggingIn: '正在登录...',
      confirmJoin: '加入组织',
      backToLogin: '返回登录',
      passwordRules: '至少12个字符，包含字母、数字和符号',
      confirmPassword: '确认密码',
    },
    onboarding: {
      welcomeTitle: '欢迎加入 Knox',
      welcomeSubtitle: '你的 AI 基础设施运维团队已就绪。',
      ctaButton: '开始设置',
      ctaLoading: '正在准备...',
      progressTitle: '引导设置',
      agentCaptain: '任务协调与团队调度',
      agentCollector: '从服务器采集基础设施数据',
      agentSupervisor: '校验数据完整性与一致性',
      agentArchitect: '分析架构与追踪依赖关系',
      agentChronicler: '将发现记录到知识图谱',
      agentCoordinator: '管理工作流与任务调度',
      step1Title: '认识你的 AI 团队',
      step2Title: '连接服务器',
      step2Desc: '在右侧 Chat 中告诉 Captain 你的服务器信息。',
      step3Title: '生成摸底报告',
      step3Desc: '你的 AI 团队将分析你的基础设施。',
      step3Analyzing: 'AI 团队正在分析你的基础设施...',
      step3Complete: '报告已就绪',
      viewReport: '查看完整报告',
      completed: 'Onboarding 完成！',
      skip: '跳过引导',
      continueGuide: '继续',
    },
    structured: {
      tool: {
        running: '执行中...',
        completed: '已完成',
        error: '出错',
        showOutput: '查看输出',
        hideOutput: '收起输出',
        loadingOutput: '加载输出中...',
        retry: '重试',
      },
      error: {
        title: '错误',
      },
      askHuman: {
        waitingForInput: '等待输入...',
        submitted: '已提交回复',
        expired: '已超时',
        answer: '回答',
        question: '问题',
        other: '其他',
        typeYourAnswer: '输入你的回答...',
        submitting: '提交中...',
        submit: '提交',
        selectPlaceholder: '请选择...',
      },
      approval: {
        approved: '已批准',
        rejected: '已拒绝',
        expired: '审批超时',
        expiredHint: '命令未执行',
        alwaysAllow: '始终允许',
        needsYourAuth: '需要你的授权',
        waitingFor: '等待 {{name}} 审批',
        processing: '处理中...',
        reject: '拒绝',
        allowOnce: '允许执行',
        approvalFailed: '审批失败',
        rejectionFailed: '拒绝操作失败',
        waitingForApproval: '等待审批中...',
        alwaysAllowScope: '始终允许将授权范围',
        complexCommandNoAlwaysAllow: '此命令使用了复杂 shell 特性（如命令替换），无法自动授权，每次执行都需要手动审批。',
      },
      threadAck: {
        processing: '正在处理中...',
        completed: '已完成',
        error: '执行出错',
      },
      systemNotice: {
        sessionCleared: '对话已重置，重新开始。',
        sessionStopped: '已停止当前处理。',
        sessionStoppedIdle: '当前没有正在执行的任务。',
        processingInterrupted: '处理已中断。',
        allStopped: '已停止：{{names}}',
        allStoppedIdle: '当前没有正在执行的任务。',
        allCleared: '所有对话已重置。',
        personaCleared: '{{name}} 的对话已重置。',
      },
      botControl: {
        stopTooltip: '停止所有正在执行的任务',
        clearSession: '清除对话',
        clearAll: '清除全部',
        confirmClear: '确定重置 {{name}} 的上下文吗？所有对话记录将被清除。',
        confirmStop: '确定停止 {{name}} 吗？如果正在执行则会被取消。',
        confirmBtn: '确定',
        cancelBtn: '取消',
      },
      nameSeparator: '、',
      search: {
        scopeThisRoom: '当前房间',
        scopeAllRooms: '所有房间',
        searchMessages: '搜索消息',
        searchInRoom: '在此房间内搜索',
        searchAllRooms: '搜索所有房间',
        noResults: '没有找到 <b>{{term}}</b> 的结果',
        noResultsHint: '请尝试其他关键词或检查拼写',
        thread: 'Thread',
        replyTo: '回复',
      },
      profile: {
        roleAdmin: '管理员',
        roleModerator: '协管员',
        roleMember: '成员',
        mention: '提及',
        stop: '停止',
        clear: '清除',
        aiBadge: 'AI',
      },
      typing: {
        thinking: '思考中...',
        oneTyping: '<bold>{{name}}</bold> 正在输入...',
        twoTyping: '<bold>{{name1}}</bold> 和 <bold>{{name2}}</bold> 正在输入...',
        manyTyping: '<bold>{{name}}</bold> 和其他 {{count}} 人正在输入...',
      },
      timeline: {
        newMessages: '新消息',
        jumpToLatest: '跳到最新',
        noMessagesYet: '暂无消息',
        suggestions: {
          title: 'Knox Team 已就绪',
          subtitle: '选择一个快速开始，或直接输入你的需求',
          baseline: {
            title: '摸底报告',
            desc: '全面了解当前系统状态',
            prompt: '对我的基础设施做一次资源发现摸底',
          },
          troubleshoot: {
            title: '排查一个生产问题',
            desc: '调查并解决生产环境异常',
            prompt: '帮我排查一个问题：',
          },
          change: {
            title: '执行一个运维变更',
            desc: '运行操作与配置变更',
            prompt: '帮我执行一个变更：',
          },
          capabilities: {
            title: '我能做哪些事？',
            desc: '了解团队能力范围',
            prompt: '介绍一下你们团队的能力，我能让你们帮我做哪些事？',
          },
        },
        inboxWelcome: {
          greetingMorning: '早上好, {{name}}',
          greetingAfternoon: '下午好, {{name}}',
          greetingEvening: '晚上好, {{name}}',
          subtitle: '你的 AI SRE 团队 — 发现、监控、排障、运维，一站式处理',
          alertSummary: {
            label: '告警摘要',
            prompt: '帮我总结当前告警',
          },
          healthCheck: {
            label: '系统巡检',
            prompt: '做一次系统健康检查',
          },
          troubleshoot: {
            label: '问题排查',
            prompt: '帮我排查一个问题：',
          },
          capabilities: {
            label: '了解能力',
            prompt: '你能帮我做什么？',
          },
        },
      },
    },
  },
};

/**
 * Knox i18n resource data for the `knox-aiops` namespace.
 * Exported so components can register them on the correct i18next instance.
 */
export const KNOX_RESOURCES = {
  en: KNOX_RESOURCES_EN['knox-aiops'],
  zh: KNOX_RESOURCES_ZH['knox-aiops'],
};

/**
 * Register Knox resources on a given i18next instance.
 */
export function addKnoxResourcesTo(i18n: { addResourceBundle: Function }) {
  i18n.addResourceBundle('en', 'knox-aiops', KNOX_RESOURCES_EN['knox-aiops'], true, true);
  i18n.addResourceBundle('zh-CN', 'knox-aiops', KNOX_RESOURCES_ZH['knox-aiops'], true, true);
  i18n.addResourceBundle('zh', 'knox-aiops', KNOX_RESOURCES_ZH['knox-aiops'], true, true);
}

/**
 * Module-level convenience: register on the imported i18next instance.
 */
export function addKnoxResources() {
  addKnoxResourcesTo(i18next);
}

// Initialize i18next with Knox resources.
i18next
  .use(initReactI18next)
  .init({
    lng: 'en',
    fallbackLng: 'en',
    ns: ['knox-aiops'],
    defaultNS: 'knox-aiops',
    resources: {
      en: KNOX_RESOURCES_EN,
      'zh-CN': KNOX_RESOURCES_ZH,
      zh: KNOX_RESOURCES_ZH,
    },
    interpolation: { escapeValue: false },
  })
  .then(() => restoreStoredLanguage());

/**
 * Restore language from localStorage if the user previously chose a non-default language.
 */
function restoreStoredLanguage() {
  const stored = localStorage.getItem('knox-language');
  if (stored && stored !== i18next.language) {
    i18next.changeLanguage(stored);
  }
}
